import React from 'react';
import QuestionRow from './QuestionRow';
import {
  OPTIONAL_COLUMNS,
  emptyQuestion,
  buildOrSibling,
  connectorLabel,
} from './templateSchema';

/**
 * Reusable question list that manages a `questions[]` array.
 * Used by SectionCard (section.questions) and by TemplateEditor (top-level
 * `structure.questions`) so both share the same behavior for:
 *   - add question
 *   - update/delete a question
 *   - "+OR" creates a paired sibling with OR connector
 *
 * Props:
 *   questions, visibleColumns, options,
 *   onChange(updatedArray),
 *   addLabel ("+ Add Question to Section A" etc.)
 *   emptyText
 */
function QuestionTable({
  questions = [],
  visibleColumns,
  options = {},
  onChange,
  addLabel = '+ Add Question',
  emptyText = 'No questions yet. Click below to add one.',
}) {
  const visibleCols = OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

  const updateAt = (idx, updated) => {
    const next = [...questions];
    next[idx] = updated;
    onChange(next);
  };

  const deleteAt = (idx) => {
    const next = [...questions];
    next.splice(idx, 1);
    // If the deleted row broke a connector chain, clean it up on the previous row.
    if (idx > 0 && next[idx - 1]?.connectorAfter && idx >= next.length) {
      next[idx - 1] = { ...next[idx - 1], connectorAfter: null };
    }
    onChange(next);
  };

  const addNew = () => {
    const q = emptyQuestion();
    q.number = String(questions.length + 1);
    onChange([...questions, q]);
  };

  /**
   * Create a new OR-paired sibling immediately after index.
   * Sets the connector on the source question to OR.
   */
  const addOrAfter = (idx) => {
    const source = questions[idx];
    const sib = buildOrSibling(source, idx);
    const next = [...questions];
    next[idx] = {
      ...source,
      connectorAfter: { type: 'OR', label: connectorLabel('OR') },
    };
    next.splice(idx + 1, 0, sib);
    onChange(next);
  };

  return (
    <>
      <table className="qp-q-table">
        <thead>
          <tr>
            <th style={{ width: 56 }}>#</th>
            <th style={{ width: 90 }}>Q.No</th>
            <th style={{ width: 110 }}>Marks</th>
            {visibleCols.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
            <th style={{ width: 160 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {questions.length === 0 ? (
            <tr>
              <td
                colSpan={3 + visibleCols.length + 1}
                style={{ textAlign: 'center', color: '#9e9e9e', padding: 16 }}
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            questions.map((q, qi) => (
              <QuestionRow
                key={q.id}
                question={q}
                siblings={questions}
                index={qi}
                visibleColumns={visibleColumns}
                options={options}
                onChange={(u) => updateAt(qi, u)}
                onDelete={() => deleteAt(qi)}
                onAddOrAfter={() => addOrAfter(qi)}
              />
            ))
          )}
        </tbody>
      </table>

      <button type="button" className="qp-add-question-btn" onClick={addNew}>
        {addLabel}
      </button>
    </>
  );
}

export default QuestionTable;
